import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import {
  AdminContent,
  AdminHeader,
  AdminSidebar,
  useAuthUser,
  useCurrentCompany,
  AddCompanyModal,
} from '@employ-engage/common-components';
import { NavMenuItem, Company } from '@employ-engage/data';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import parseInt from 'lodash.parseint';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface AdminPageProps {}

export function AdminPage(props: AdminPageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const TRANSITION_DURATION_CLASS = 'duration-300';
  const { signout } = useAuthUser();
  const { currentCompany: company } = useCurrentCompany();
  const { t } = useTranslation(['common']);
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState<Company | null>(company);

  useEffect(() => {
    if (company) {
      setCompanyToEdit(company);
    }
  }, [company]);

  const navMenu: (NavMenuItem | 'horizontal-rule' | null)[] = [];

  navMenu.push({
    // Every other item here is translated; this one was not, which is why the
    // nav read "Dashboard" in the middle of an otherwise Hebrew sidebar.
    title: t('Dashboard'),
    buttonClassName: 'dashboard-nav-button',
    iconClassName: 'fa-chart-line',
    to: `/${company?.friendlyCompanyId}/admin/dashboard`,
    current: !!matchRoutes(
      [{ path: `/${company?.friendlyCompanyId}/admin/dashboard` }],
      location
    ),
  });

  navMenu.push(
    {
      title: t('Forms'),
      buttonClassName: 'forms-nav-button',
      iconClassName: 'fa-list-check',
      to: `/${company?.friendlyCompanyId}/admin/forms`,
      current: !!matchRoutes(
        [{ path: `/${company?.friendlyCompanyId}/admin/forms` }],
        location
      ),
    },
    {
      title: t('Templates'),
      buttonClassName: 'templates-nav-button',
      iconClassName: 'fa-table-layout',
      to: `/${company?.friendlyCompanyId}/admin/templates`,
      current: !!matchRoutes(
        [{ path: `/${company?.friendlyCompanyId}/admin/templates` }],
        location
      ),
    },
    {
      title: t('Reports'),
      buttonClassName: 'reports-nav-button',
      iconClassName: 'fa-file-chart-column',
      to: `/${company?.friendlyCompanyId}/admin/reports`,
      current: !!matchRoutes(
        [
          { path: `/${company?.friendlyCompanyId}/admin/reports` },
          {
            path: `/${company?.friendlyCompanyId}/admin/reports/report-builder`,
          },
        ],
        location
      ),
    },
    {
      title: t('Employees'),
      buttonClassName: 'employees-nav-button',
      iconClassName: 'fa-user-group',
      to: `/${company?.friendlyCompanyId}/admin/employees`,
      current: !!matchRoutes(
        [{ path: `/${company?.friendlyCompanyId}/admin/employees` }],
        location
      ),
    },
    {
      title: t('Settings'),
      buttonClassName: 'settings-nav-button',
      iconClassName: 'fa-gear',
      to: `/${company?.friendlyCompanyId}/admin/settings`,
      current: !!matchRoutes(
        [
          { path: `/${company?.friendlyCompanyId}/admin/settings` },
          { path: `/${company?.friendlyCompanyId}/admin/settings/team` },
          {
            path: `/${company?.friendlyCompanyId}/admin/settings/delete-company`,
          },
        ],
        location
      ),
    },
    'horizontal-rule',
    {
      title: t('Logout'),
      buttonClassName: 'logout-nav-button',
      iconClassName: 'fa-right-from-bracket',
      onClick: () => {
        signout()
          .then(() => {
            navigate('/', { state: nanoid() });
          })
          .catch((error) => {
            console.error(error);
          });
      },
    }
  );

  const [adminPageElementRef, setAdminPageElementRef] =
    useState<HTMLElement | null>(null);
  const [adminSidebarElementRef, setAdminSidebarElementRef] =
    useState<HTMLElement | null>(null);
  const [adminHeaderElementRef, setAdminHeaderElementRef] =
    useState<HTMLElement | null>(null);

  let title = '';
  const item = navMenu.find((item) =>
    item !== 'horizontal-rule' ? item?.current : false
  );
  if (item) {
    title = (item as NavMenuItem)?.title || '';
  }

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);

  const updateContent = (
    adminPageElementRef: HTMLElement | null,
    adminHeaderElementRef: HTMLElement | null,
    adminSidebarElementRef: HTMLElement | null
  ) => {
    if (adminPageElementRef && adminHeaderElementRef) {
      setContentHeight(
        adminPageElementRef.clientHeight - adminHeaderElementRef.clientHeight
      );
    }

    if (adminSidebarElementRef && adminPageElementRef) {
      setContentWidth(
        adminPageElementRef.clientWidth - adminSidebarElementRef.clientWidth
      );
    }
  };

  const handleResize = () =>
    updateContent(
      adminPageElementRef,
      adminHeaderElementRef,
      adminSidebarElementRef
    );

  useEffect(() => {
    if (
      adminPageElementRef &&
      adminHeaderElementRef &&
      adminSidebarElementRef
    ) {
      window.addEventListener('resize', handleResize);
      handleResize();
    }
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [adminPageElementRef, adminSidebarElementRef, adminHeaderElementRef]);

  return (
    <div
      ref={setAdminPageElementRef}
      className="flex flex-row font-['Poppins'] fixed top-0 left-0 w-[100vw]"
    >
      <AdminSidebar
        transitionDurationClass={TRANSITION_DURATION_CLASS}
        ref={setAdminSidebarElementRef}
        navMenu={navMenu}
        isCollapsed={isCollapsed}
        setIsCollapsed={(isCollapsed) => {
          setIsCollapsed(isCollapsed);
          setTimeout(() => {
            updateContent(
              adminPageElementRef,
              adminHeaderElementRef,
              adminSidebarElementRef
            );
          }, parseInt(TRANSITION_DURATION_CLASS.replace('duration-', ''), 10));
        }}
      />
      <div className="flex-grow">
        <AdminHeader ref={setAdminHeaderElementRef} title={title} />
        <AdminContent
          transitionDurationClass={TRANSITION_DURATION_CLASS}
          contentHeight={contentHeight}
          contentWidth={contentWidth}
        />
      </div>
      {isAddCompanyModalOpen && companyToEdit?.id && (
        <AddCompanyModal
          isFirstTimeSetup
          open={isAddCompanyModalOpen}
          setOpen={setIsAddCompanyModalOpen}
          companySelectedForEditing={companyToEdit}
          setCompanySelectedForEditing={setCompanyToEdit}
        />
      )}
    </div>
  );
}

export default AdminPage;
